import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import {
    ExecutionLogFilterOptions,
    ExecutionLogResponse,
    ExecutionLogSummaryResponse,
} from "../../../models/bridge/execution-log.model";

/**
 * Service for 4Bridge Execution Logs API
 * Endpoint: /v1/4bridge/execution-logs
 */
@Injectable({
    providedIn: 'root'
})
export class BridgeExecutionLogService {
    private readonly baseUrl: string;

    constructor(private httpClient: HttpClient) {
        this.baseUrl = `${environment.api.API_ENDPOINT}/4bridge/execution-logs`;
    }

    /**
     * Get execution logs with optional filters.
     * If no taskId is provided, returns all logs for the tenant.
     */
    public getLogs(filters?: ExecutionLogFilterOptions): Observable<ExecutionLogResponse[]> {
        let params = new HttpParams();

        if (filters) {
            if (filters.taskId) {
                params = params.set('taskId', filters.taskId);
            }
            if (filters.status) {
                params = params.set('status', filters.status);
            }
            if (filters.from) {
                params = params.set('from', filters.from.toISOString());
            }
            if (filters.to) {
                params = params.set('to', filters.to.toISOString());
            }
            if (filters.limit) {
                params = params.set('limit', filters.limit.toString());
            }
        }

        return this.httpClient
            .get<ExecutionLogResponse[]>(this.baseUrl, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Get execution log summary grouped by task.
     * Returns aggregated stats for top integrations.
     */
    public getSummary(from?: Date, to?: Date): Observable<ExecutionLogSummaryResponse[]> {
        let params = new HttpParams();
        if (from) {
            params = params.set('from', from.toISOString());
        }
        if (to) {
            params = params.set('to', to.toISOString());
        }

        return this.httpClient
            .get<ExecutionLogSummaryResponse[]>(`${this.baseUrl}/summary`, { params })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Get a single execution log by ID, including details (individual record errors).
     */
    public getById(id: string): Observable<ExecutionLogResponse> {
        return this.httpClient
            .get<ExecutionLogResponse>(`${this.baseUrl}/${id}`)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An error occurred';
        if (error.error instanceof ErrorEvent) {
            errorMessage = error.error.message;
        } else {
            errorMessage = error.error?.message || error.message || 'Server error';
        }
        console.error('Execution Log API Error:', errorMessage, error);
        return throwError(() => ({ message: errorMessage, status: error.status, error: error.error }));
    }
}
